import { useState } from "react";
import {
  Box, Typography, Chip, Stack, Button, IconButton, Divider, Tooltip, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, CircularProgress,
  FormControlLabel, Checkbox,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

const statusInfo = (s) =>
  s === "D" ? { label: "Deferido", color: "success" }
  : s === "R" ? { label: "Recusado", color: "error" }
  : { label: "Em análise", color: "warning" };

// Checklist de documentos agrupado por categoria.
// modo: "candidato" (enviar/reenviar/excluir) ou "analista" (validar no modal).
export default function DocumentosChecklist({
  tipos, anexos, modo = "candidato",
  onUpload, onExcluir, onBaixar, onAvaliar, fetchBlob,
}) {
  const grupos = [...new Set(tipos.map((t) => t.Grupo || "Outros"))];
  const anexosDoTipo = (idTipo) => anexos.filter((a) => a.IdTipoDocumento === idTipo);
  const avulsos = anexos.filter((a) => !a.IdTipoDocumento);

  // Modal de visualização
  const [ver, setVer] = useState(null);       // anexo em visualização
  const [url, setUrl] = useState(null);       // blob URL
  const [ct, setCt] = useState("");           // content type
  const [carregando, setCarregando] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [recusando, setRecusando] = useState(false);   // modal do motivo da recusa
  const [notificar, setNotificar] = useState(true);
  const [salvando, setSalvando] = useState(false);

  const abrir = async (a) => {
    setVer(a); setMotivo(a.MotivoRecusa || ""); setUrl(null); setCt(a.ContentType || ""); setCarregando(true);
    try {
      const blob = await fetchBlob(a.Id);
      setUrl(URL.createObjectURL(blob));
      setCt(a.ContentType || blob.type || "");
    } catch {
      setUrl(null);
    } finally { setCarregando(false); }
  };
  const fechar = () => {
    if (url) URL.revokeObjectURL(url);
    setVer(null); setUrl(null); setMotivo(""); setRecusando(false);
  };

  const deferir = async () => { await onAvaliar(ver, "D"); fechar(); };

  // Recusar abre um modal próprio: antes o campo do motivo ficava embaixo da
  // pré-visualização (70vh) e saía da tela, dando a impressão de não existir.
  const abrirRecusa = () => { setMotivo(ver?.MotivoRecusa || ""); setNotificar(true); setRecusando(true); };

  const confirmarRecusa = async () => {
    if (!motivo.trim()) return;
    setSalvando(true);
    try { await onAvaliar(ver, "R", motivo.trim(), notificar); fechar(); }
    finally { setSalvando(false); }
  };

  const ehImagem = ct.startsWith("image/");
  const ehPdf = ct === "application/pdf";

  const Arquivo = ({ a }) => {
    const st = statusInfo(a.Status);
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.5, pl: { sm: 2 } }}>
        <Chip size="small" label={st.label} color={st.color} variant="outlined" />
        <Typography variant="body2" sx={{ flex: 1, wordBreak: "break-all" }}>{a.NomeOriginal}</Typography>
        {a.Status === "R" && a.MotivoRecusa && (
          <Tooltip title={`Motivo: ${a.MotivoRecusa}`}>
            <Typography variant="caption" color="error" sx={{ maxWidth: 200, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {a.MotivoRecusa}
            </Typography>
          </Tooltip>
        )}
        {fetchBlob && (
          <Tooltip title="Visualizar"><IconButton size="small" color="primary" onClick={() => abrir(a)}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
        )}
        <IconButton size="small" onClick={() => onBaixar(a)}><DownloadIcon fontSize="small" /></IconButton>
        {modo === "candidato" && (
          <Tooltip title={a.Status === "D" ? "Documento deferido não pode ser removido" : "Remover"}>
            <span>
              <IconButton size="small" color="error" disabled={a.Status === "D"} onClick={() => onExcluir(a)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Stack>
    );
  };

  return (
    <Box>
      {modo === "candidato" && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Anexe os documentos exigidos. Os marcados como <b>obrigatórios</b> são necessários para enviar a inscrição.
          Documentos <b>recusados</b> devem ser reenviados nesta mesma inscrição.
        </Alert>
      )}

      {grupos.map((grupo) => (
        <Box key={grupo} sx={{ mb: 2.5 }}>
          <Typography variant="subtitle2" color="primary">{grupo}</Typography>
          <Divider sx={{ mb: 1, mt: 0.5 }} />
          {tipos.filter((t) => (t.Grupo || "Outros") === grupo).map((t) => {
            const lista = anexosDoTipo(t.Id);
            return (
              <Box key={t.Id} sx={{ mb: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 220 }}>
                    {t.Descricao}{" "}
                    {t.Obrigatorio
                      ? <Chip size="small" label="Obrigatório" color="default" sx={{ ml: 0.5 }} />
                      : <Chip size="small" label="Se aplicável" variant="outlined" sx={{ ml: 0.5 }} />}
                  </Typography>
                  {modo === "candidato" && (
                    <Button component="label" size="small" variant="outlined" startIcon={<UploadFileIcon />}>
                      Anexar PDF
                      <input type="file" hidden multiple accept="application/pdf,.pdf"
                        onChange={async (e) => { const fs = Array.from(e.target.files || []); e.target.value = ""; for (const f of fs) await onUpload(t, f); }} />
                    </Button>
                  )}
                </Stack>
                {lista.map((a) => <Arquivo key={a.Id} a={a} />)}
                {lista.length === 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ pl: { sm: 2 } }}>Nenhum arquivo anexado.</Typography>
                )}
              </Box>
            );
          })}
        </Box>
      ))}

      {avulsos.length > 0 && (
        <Box>
          <Typography variant="subtitle2" color="primary">Outros documentos</Typography>
          <Divider sx={{ mb: 1, mt: 0.5 }} />
          {avulsos.map((a) => <Arquivo key={a.Id} a={a} />)}
        </Box>
      )}

      {/* Modal de visualização + validação */}
      <Dialog open={!!ver} onClose={fechar} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {ver?.NomeOriginal}
          {ver && <Chip size="small" {...statusInfo(ver.Status)} label={statusInfo(ver.Status).label} color={statusInfo(ver.Status).color} />}
        </DialogTitle>
        <DialogContent dividers>
          {carregando ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
          ) : !url ? (
            <Alert severity="warning">Não foi possível pré-visualizar. Use o botão Baixar.</Alert>
          ) : ehImagem ? (
            <Box component="img" src={url} alt="documento" sx={{ width: "100%", maxHeight: "70vh", objectFit: "contain" }} />
          ) : ehPdf ? (
            <Box component="iframe" title="documento" src={url} sx={{ width: "100%", height: "70vh", border: "none" }} />
          ) : (
            <Alert severity="info">Pré-visualização não disponível para este tipo de arquivo. Use o botão Baixar.</Alert>
          )}

        </DialogContent>
        <DialogActions>
          {url && <Button onClick={() => onBaixar(ver)} startIcon={<DownloadIcon />}>Baixar</Button>}
          <Box sx={{ flex: 1 }} />
          {modo === "analista" && (
            <>
              <Button color="error" variant="outlined" startIcon={<CancelIcon />} onClick={abrirRecusa}>Recusar</Button>
              <Button color="success" variant="contained" startIcon={<CheckCircleIcon />} onClick={deferir}>Deferir</Button>
            </>
          )}
          <Button onClick={fechar}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Motivo da recusa — modal próprio, para o campo não ficar escondido */}
      <Dialog open={recusando} onClose={() => setRecusando(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Recusar documento</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {ver?.NomeOriginal}
          </Typography>
          <TextField
            autoFocus fullWidth multiline minRows={3} label="Motivo da recusa"
            placeholder="Explique o que precisa ser corrigido para o candidato reenviar."
            value={motivo} onChange={(e) => setMotivo(e.target.value)}
            error={!motivo.trim()} helperText={!motivo.trim() ? "Obrigatório — o candidato verá este texto." : " "}
          />
          <FormControlLabel
            control={<Checkbox checked={notificar} onChange={(e) => setNotificar(e.target.checked)} />}
            label="Notificar o candidato agora (WhatsApp/e-mail)" />
          <Alert severity="info" sx={{ mt: 1 }}>
            A notificação lista <b>todos</b> os documentos recusados desta ficha. Ao recusar vários
            em sequência, desmarque e use o botão <b>Notificar indeferimento</b> no fim da análise
            para enviar uma única mensagem.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecusando(false)}>Cancelar</Button>
          <Button color="error" variant="contained" startIcon={<CancelIcon />}
            disabled={!motivo.trim() || salvando} onClick={confirmarRecusa}>
            {salvando ? "Salvando…" : "Confirmar recusa"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
